import { dateUSFormat } from '../../../utils/dateTimeUtils'
import moment from 'moment'
import {
  getPCCAllergies,
  getPCCConditions,
  getPCCCoverages,
  getPCCData,
  getPCCImmunizations,
  getPCCMedications,
  getPCCObservations,
  getPCCReport,
} from '../../../helper/pccApiHelper'
import { capitalize } from 'lodash'

/**
 * Figure out where a patient is integrated from
 *
 * @param patientChargeItem the incoming charge with integration information
 * @param secondaryPatientChargeItem a hopefully not needed secondary backup charge with integration information
 * @return {{isConnected: boolean, connectedTitle: string}}
 */
export function calculatePatientIntegration(patientChargeItem, secondaryPatientChargeItem = null) {
  let isConnected = false
  let connectedTitle = ''
  // possible error by not ever using secondary (PatientDetails.state.pdata here?
  if (
    patientChargeItem?.points_click_care_patient ||
    patientChargeItem?.point_click_care_ext_patient_id
  ) {
    isConnected = true
    connectedTitle = 'PCC'
  }
  if (
    patientChargeItem?.matrixcare_ext_patient_id ||
    patientChargeItem?.matrix_care_ext_patient_id ||
    patientChargeItem?.external_ids?.matrix
  ) {
    isConnected = true
    connectedTitle = 'MatrixCare'
  }
  if (patientChargeItem?.sigma_care_ext_patient_id || patientChargeItem?.external_ids?.sigmacare) {
    isConnected = true
    connectedTitle = 'SigmaCare'
  }
  if (patientChargeItem?.external_ids?.hca?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'HCA'
  }
  if (patientChargeItem?.external_ids?.yrmc?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'YRMC'
  }
  if (patientChargeItem?.external_ids?.integration_anova?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'VUMC'
  }
  if (patientChargeItem?.external_ids?.vumc?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'VUMC'
  }
  if (patientChargeItem?.external_ids?.primehealth?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Prime Healthcare'
  }
  if (patientChargeItem?.external_ids?.anovahealth?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Life Point'
  }
  if (patientChargeItem?.external_ids?.pioneer?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'AHT'
  }
  if (patientChargeItem?.external_ids?.wellsky?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Wellsky'
  }
  if (patientChargeItem?.external_ids?.epic?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Epic'
  }
  if (patientChargeItem?.external_ids?.mtsinai?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Mt Sinai'
  }
  if (patientChargeItem?.external_ids?.medimobile?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Medimobile'
  }
  if (patientChargeItem?.external_ids?.umc?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'UMC'
  }
  if (patientChargeItem?.external_ids?.mb?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Saint Marys'
  }
  if (patientChargeItem?.external_ids?.unity?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Unity'
  }
  if (patientChargeItem?.external_ids?.demi?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'DEMI'
  }
  if (patientChargeItem?.external_ids?.encompass?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'Encompass'
  }
  if (patientChargeItem?.external_ids?.reliablehealth?.ext_patient_id) {
    isConnected = true
    connectedTitle = 'ReliableHealth'
  }

  return { isConnected, connectedTitle }
}

export function calculateAdmissionDate(item, defaultValue = '') {
  if (item?.admitPatientEvent?.date) {
    return moment.utc(item.admitPatientEvent.date).format('MM/DD/YYYY')
  } else if (item?.admission_date) {
    return dateUSFormat(item.admission_date)
  }

  return defaultValue
}

export function calculateDischargeDate(item, defaultValue = '') {
  if (item?.dischargePatientEvent?.date) {
    return moment.utc(item.dischargePatientEvent.date).format('MM/DD/YYYY')
  } else if (item?.discharge_date) {
    return dateUSFormat(item.discharge_date)
  }

  return defaultValue
}

// transforms an array of demographic codes into the user readable display string
export function getDemographicDisplayByCodeArray(demographicArray, codeArray) {
  const titles = []
  demographicArray.forEach((obj) => {
    if (codeArray.includes(obj.code)) {
      titles.push(obj.display)
    }
  })

  return titles.join(', ')
}

// takes a demographic code and returns the user readable string
export function getDemographicDisplayByCode(demographicArray, code) {
  for (const obj of demographicArray) {
    if (code === obj.code) {
      return obj.display
    }
  }

  return null
}

// takes a list of single select options and transforms the data to match
// expected coreUI specs
export function formatForSingleSelect(arr = [], value) {
  let selectedValueExists = false
  const formattedOptions = arr.map((x) => {
    if (x.code === value) {
      selectedValueExists = true
    }
    return {
      ...x,
      value: x.code,
      label: x.display,
    }
  })

  if (!selectedValueExists) {
    formattedOptions.unshift({
      value: '',
      label: '',
      disabled: true,
      selected: true,
    })
  }

  return formattedOptions
}

// takes a list of multiselect options and transforms the data to match
// expected coreUI specs
export function formatForMultiSelect(arr = [], selectedValues = []) {
  return arr.map((x) => {
    const isSelected = selectedValues.includes(x.code)
    return {
      value: x.code,
      text: x.display,
      selected: isSelected,
    }
  })
}

export async function getPatientClinicalInfo(patientId, onSuccess) {
  const observation = []
  let medDiagnosis = []
  const allergies = []
  const immunizations = []

  const results = await Promise.allSettled([
    getPCCObservations(patientId),
    getPCCConditions(patientId),
    getPCCAllergies(patientId),
    getPCCImmunizations(patientId),
  ])
  const [obsResult, condResult, allerResult, immuResult] = results.map((result) => {
    if (result.status === 'fulfilled') {
      return result.value
    }
    console.error(result.reason)
    return null
  })

  if (obsResult && obsResult.length > 0) {
    for (let i = 0; i < obsResult.length; i += 2) {
      observation.push([obsResult[i], obsResult[i + 1]])
    }
  }

  if (condResult && condResult.length > 0) {
    medDiagnosis = condResult
  }

  if (allerResult && allerResult.length > 0) {
    for (let i = 0; i < allerResult.length; i += 2) {
      allergies.push([allerResult[i], allerResult[i + 1]])
    }
  }

  if (immuResult && immuResult.length > 0) {
    for (let i = 0; i < immuResult.length; i += 2) {
      immunizations.push([immuResult[i], immuResult[i + 1]])
    }
  }

  onSuccess({
    observation: observation,
    medDiagnosis: medDiagnosis,
    allergies: allergies,
    immunizations: immunizations,
  })
}

export async function getPatientMedInfo(patientId, onSuccess) {
  let meds = []
  const medResults = await getPCCMedications(patientId)

  if (medResults && medResults.length > 0) {
    meds = medResults
  }

  onSuccess(meds)
}

export async function getPatientRadiologyLabInfo(patientId, issuedDate = null, onSuccess) {
  const labsAndRadioInfo = {}
  await getPCCReport(patientId, 'radiology', issuedDate).then((res) => {
    if (res && res.length > 0) {
      labsAndRadioInfo.radiology = res
    }
  })
  await getPCCReport(patientId, 'laboratory', issuedDate).then((res) => {
    if (res && res.length > 0) {
      labsAndRadioInfo.laboratory = res
    }
  })
  onSuccess(labsAndRadioInfo)
}

export async function getPatientInsuranceInfo(patientId, onSuccess) {
  const pccData = await getPCCData(patientId)
  const pccCoverages = await getPCCCoverages(patientId)
  if (pccData) {
    onSuccess({ ...pccData, coverages: pccCoverages })
  }
}

export function formatName(item) {
  const first = item?.patient_firstname || ''
  const middle = item?.patient_middlename || ''
  const last = item?.patient_lastname || ''
  return `${last.toUpperCase()}, ${capitalize(first)} ${capitalize(middle)}`
}
